/**
 * Orígenes para llamar al API del backend.
 * 1) Mismo host que el SPA (proxy nginx, Vite proxy, etc.)
 * 2) `VITE_API_URL` si existe y es distinto (solo si el primero falla por red)
 */
export function buildApiUrlCandidates(path: string): string[] {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const base = (import.meta.env.VITE_API_URL || '').trim().replace(/\/+$/, '')
  const fromVite = base ? `${base}${normalized}` : null
  const fromWindow =
    typeof window !== 'undefined' ? `${window.location.origin}${normalized}` : null

  const ordered: string[] = []
  if (fromWindow) ordered.push(fromWindow)
  if (fromVite && !ordered.includes(fromVite)) ordered.push(fromVite)
  if (ordered.length === 0) ordered.push(normalized)
  return ordered
}

/** Igual que `buildApiUrlCandidates`[0] o ruta relativa; útil para imports puntuales. */
export function resolveApiPath(path: string): string {
  const candidates = buildApiUrlCandidates(path)
  return candidates[0] ?? path
}

/**
 * `fetch` al API probando candidatos en orden: el primero que responda HTTP (aunque sea 503) gana;
 * si todos lanzan por red, relanza el último error.
 *
 * Detecta y descarta respuestas SPA fallback (200 text/html): cuando el servidor
 * del frontend no tiene proxy /api, nginx devuelve index.html para cualquier ruta
 * desconocida. Como nuestro API nunca devuelve HTML, una respuesta 200 text/html
 * indica que hemos caído en el fallback del SPA y debemos probar el siguiente origen.
 */
export async function fetchFromApi(path: string, init?: RequestInit): Promise<Response> {
  const urls = buildApiUrlCandidates(path)
  let lastErr: unknown
  let spaFallback: Response | null = null
  for (const url of urls) {
    try {
      const res = await fetch(url, { ...init, credentials: 'omit' })
      if (res.ok && (res.headers.get('content-type') || '').includes('text/html')) {
        spaFallback = res
        continue
      }
      return res
    } catch (e) {
      lastErr = e
    }
  }
  if (spaFallback) return spaFallback
  if (lastErr instanceof Error) throw lastErr
  throw new Error('Failed to fetch')
}
